/**
 * Local persistence for generated plans.
 * Plans are stored in localStorage so they survive page reloads and tab closes.
 */

import type { GeneratedPlan } from './planningApi';

export interface PlanHistoryEntry {
  id: string;
  description: string;
  plan: GeneratedPlan;
  createdAt: string;
}

function storageKey(key: string) {
  return `plan-history:${key}`;
}

/** Migrate old repo-keyed entries into the org key on first access. */
function migrateRepoKeys(orgKey: string): PlanHistoryEntry[] {
  const migrated: PlanHistoryEntry[] = [];
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith('plan-history:')) continue;
    const suffix = key.slice('plan-history:'.length);
    // Old repo keys contain a slash (owner/repo); org keys don't
    if (!suffix.includes('/')) continue;
    try {
      const entries = JSON.parse(localStorage.getItem(key)!) as PlanHistoryEntry[];
      migrated.push(...entries);
      keysToRemove.push(key);
    } catch { /* skip corrupt entries */ }
  }
  if (migrated.length > 0) {
    // Merge into org key
    const existing = readAll(orgKey);
    const existingIds = new Set(existing.map((e) => e.id));
    const merged = [...existing, ...migrated.filter((e) => !existingIds.has(e.id))];
    localStorage.setItem(storageKey(orgKey), JSON.stringify(merged));
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  }
  return migrated;
}

let migrationDone = false;

function readAll(key: string): PlanHistoryEntry[] {
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (!raw) return [];
    return JSON.parse(raw) as PlanHistoryEntry[];
  } catch {
    return [];
  }
}

function writeAll(repo: string, entries: PlanHistoryEntry[]) {
  localStorage.setItem(storageKey(repo), JSON.stringify(entries));
}

export const planHistoryStore = {
  list(key: string): PlanHistoryEntry[] {
    if (!migrationDone) {
      migrateRepoKeys(key);
      migrationDone = true;
    }
    return readAll(key).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  },

  save(repo: string, description: string, plan: GeneratedPlan): PlanHistoryEntry {
    const entry: PlanHistoryEntry = {
      id: crypto.randomUUID(),
      description,
      plan,
      createdAt: new Date().toISOString(),
    };
    const entries = readAll(repo);
    entries.unshift(entry);
    writeAll(repo, entries);
    return entry;
  },

  remove(repo: string, id: string) {
    const entries = readAll(repo).filter((e) => e.id !== id);
    writeAll(repo, entries);
  },

  get(repo: string, id: string): PlanHistoryEntry | undefined {
    return readAll(repo).find((e) => e.id === id);
  },
};
